import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtDate, exportCSV } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input,
  Select, Textarea, Empty,
} from "@/components/primitives";
import ProjectSelect from "@/components/ProjectSelect";
import SmartInput from "@/components/SmartInput";
import SearchableSelect from "@/components/SearchableSelect";
import { toast } from "sonner";
import { Plus, Trash, DownloadSimple, FloppyDisk, Eye } from "@phosphor-icons/react";

const TODAY = new Date().toISOString().slice(0, 10);
const BLANK = {
  issue_date: TODAY,
  issued_to_name: "",
  employee_code: "",
  employee_id: "",
  department: "",
  project_code: "",
  cost_center: "",
  purpose: "",
  notes: "",
  items: [{ _lineId: crypto.randomUUID(), sku: "", name: "", quantity: 1, unit: "ea", equipment_type: "", equipment_ref: "" }],
};

export default function StockIssuances() {
  const { user } = useAuth();
  const canManage = ["admin", "manager", "director"].includes(user.role);
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const [rs, inv] = await Promise.all([
        api.get("/stock-issuances"),
        api.get("/inventory"),
      ]);
      setRows(rs.data);
      setInventory(inv.data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  // Reference data — loaded once the modal opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [emps, dep, cc, trk, mch] = await Promise.all([
          api.get("/employees").catch(() => ({ data: [] })),
          api.get("/lookups/department").catch(() => ({ data: [] })),
          api.get("/lookups/cost_center").catch(() => ({ data: [] })),
          api.get("/trucks").catch(() => ({ data: [] })),
          api.get("/machines").catch(() => ({ data: [] })),
        ]);
        setEmployees(emps.data || []);
        setDepartments(dep.data || []);
        setCostCenters(cc.data || []);
        setTrucks(trk.data || []);
        setMachines(mch.data || []);
      } catch (_) { /* graceful — non-HR users still get free-text */ }
    })();
  }, [open]);

  const inventoryOpts = useMemo(() =>
    inventory.map((i) => ({ value: i.sku, label: `${i.name} (${i.sku})`, hint: `${i.quantity ?? 0} ${i.unit || ""}`.trim(), item: i })),
  [inventory]);

  const employeeOpts = useMemo(() =>
    (employees || []).map((e) => ({
      value: e.id, label: `${e.full_name || e.employee_code}`,
      hint: e.employee_code || "",
      emp: e,
    })), [employees]);

  const departmentOpts = useMemo(() =>
    (departments || []).map((d) => ({ value: d.name, label: d.name, hint: d.code || "" })), [departments]);

  const costCenterOpts = useMemo(() =>
    (costCenters || []).map((c) => ({ value: c.name, label: c.name, hint: c.code || "" })), [costCenters]);

  const equipmentOpts = useMemo(() => {
    const t = (trucks || []).map((x) => ({ value: `truck:${x.id}`, label: `${x.plate || x.fleet_no || "—"} · ${x.make || ""} ${x.model || ""}`.trim(), hint: "TRUCK", refType: "truck", refId: x.id }));
    const m = (machines || []).map((x) => ({ value: `machine:${x.id}`, label: `${x.asset_tag || "—"} · ${x.name || ""}`.trim(), hint: (x.category || "MACHINE").toUpperCase(), refType: "machine", refId: x.id }));
    return [...t, ...m];
  }, [trucks, machines]);

  function setItem(i, patch) {
    const items = [...form.items];
    items[i] = { ...items[i], ...patch };
    setForm({ ...form, items });
  }

  function pickSku(i, sku) {
    const inv = inventory.find((x) => x.sku === sku);
    if (inv) {
      setItem(i, { sku: inv.sku, name: inv.name, unit: inv.unit || "ea" });
    } else {
      setItem(i, { sku, name: "" });
    }
  }

  function pickEmployee(empId, opt) {
    const e = opt?.emp || employees.find((x) => x.id === empId);
    setForm({
      ...form,
      employee_id: e?.id || empId || "",
      issued_to_name: e?.full_name || form.issued_to_name,
      employee_code: e?.employee_code || form.employee_code,
      department: e?.department || form.department,
    });
  }

  function isSpareCategory(sku) {
    const inv = inventory.find((x) => x.sku === sku);
    const c = (inv?.category || "").toLowerCase();
    return c.includes("spare") || c.includes("part");
  }

  async function submit() {
    if (!form.issued_to_name || !form.purpose || !form.items.length) {
      toast.error("Recipient, purpose and at least one item are required");
      return;
    }
    if (form.items.some((it) => !it.sku || Number(it.quantity) <= 0)) {
      toast.error("Every line item needs an SKU and a positive quantity");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({
          sku: it.sku,
          name: it.name,
          quantity: Number(it.quantity),
          unit: it.unit,
          equipment_type: it.equipment_type || null,
          equipment_ref: it.equipment_ref || null,
        })),
      };
      await api.post("/stock-issuances", payload);
      toast.success("Stock issued & inventory updated");
      setOpen(false);
      setForm(BLANK);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  }

  async function voidIssuance(rec) {
    if (!window.confirm(`Void issuance ${rec.number} and restore stock?`)) return;
    try {
      await api.delete(`/stock-issuances/${rec.id}`);
      toast.success("Issuance voided, stock restored");
      load();
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div>
      <PageHeader
        title="Stores Issuance"
        subtitle="Issue inventory items to employees, projects or departments. Stock is decremented automatically."
        actions={
          <>
            <BtnGhost onClick={() => rows.length && exportCSV("stock-issuances.csv", rows)}
              data-testid="issuance-export">
              <DownloadSimple size={12} weight="bold" /> Export CSV
            </BtnGhost>
            <BtnPrimary onClick={() => { setForm(BLANK); setOpen(true); }} data-testid="issuance-new">
              <Plus size={12} weight="bold" /> New Issuance
            </BtnPrimary>
          </>
        }
      />

      {loading ? (
        <div className="phizo-label">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty label="No stock issuances yet. Click 'New Issuance' to issue stores to an employee, project or department." />
      ) : (
        <div className="phizo-card overflow-x-auto">
          <table className="phizo-table">
            <thead>
              <tr>
                <th>Number</th><th>Date</th><th>Issued To</th>
                <th>Employee / Project</th><th>Purpose</th>
                <th className="text-right">Lines</th>
                <th className="text-right">Total Qty</th>
                <th>Issued By</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const totalQty = (r.items || []).reduce((s, it) => s + Number(it.quantity || 0), 0);
                return (
                  <tr key={r.id} data-testid={`issuance-row-${r.number}`}
                    onClick={() => setViewOpen(r)}
                    className="cursor-pointer hover:bg-[#f4f4f5]">
                    <td className="mono font-semibold">{r.number}</td>
                    <td className="text-sm">{fmtDate(r.issue_date)}</td>
                    <td className="font-medium">{r.issued_to_name}</td>
                    <td className="text-sm text-[#52525b] mono">{r.employee_code || r.project_code || "—"}</td>
                    <td className="text-sm max-w-xs truncate" title={r.purpose}>{r.purpose}</td>
                    <td className="text-right mono">{r.items?.length || 0}</td>
                    <td className="text-right mono font-semibold">{totalQty}</td>
                    <td className="text-sm text-[#52525b]">{r.issued_by_name}</td>
                    <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <BtnGhost onClick={() => setViewOpen(r)}
                        data-testid={`issuance-view-${r.number}`}>
                        <Eye size={12} weight="bold" />
                      </BtnGhost>
                      {canManage && (
                        <BtnGhost onClick={() => voidIssuance(r)}
                          className="text-[#FF2A2A]"
                          data-testid={`issuance-void-${r.number}`}>
                          <Trash size={12} weight="bold" />
                        </BtnGhost>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* New / edit modal */}
      <Modal open={open} onClose={() => setOpen(false)} size="lg"
        title="New Stores Issuance"
        actions={
          <>
            <BtnSecondary onClick={() => setOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary onClick={submit} disabled={saving} data-testid="issuance-submit">
              <FloppyDisk size={13} weight="bold" /> {saving ? "Saving…" : "Save Issuance"}
            </BtnPrimary>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Date *">
            <Input type="date" value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              data-testid="issuance-date" />
          </Field>
          <Field label="Recipient *" hint="Search by name or employee code">
            <SearchableSelect
              value={form.employee_id}
              onChange={pickEmployee}
              options={employeeOpts}
              placeholder="Start typing a name…"
              allowFreeText={employeeOpts.length === 0}
              testId="issuance-recipient"
            />
          </Field>
          <Field label="Employee Code" hint="Auto-filled from Recipient">
            <Input value={form.employee_code} readOnly disabled
              data-testid="issuance-empcode"
              placeholder="—" />
          </Field>
          <Field label="Department" hint="Master list — pick or leave blank">
            <SearchableSelect
              value={form.department}
              onChange={(v) => setForm({ ...form, department: v })}
              options={departmentOpts}
              placeholder="Search departments…"
              allowFreeText
              clearable
              testId="issuance-department"
            />
          </Field>
          <Field label="Cost Centre">
            <SearchableSelect
              value={form.cost_center}
              onChange={(v) => setForm({ ...form, cost_center: v })}
              options={costCenterOpts}
              placeholder="Search cost centres…"
              allowFreeText
              clearable
              testId="issuance-cost-center"
            />
          </Field>
          <Field label="Project / Cost-code">
            <ProjectSelect value={form.project_code}
              onChange={(v) => setForm({ ...form, project_code: v })}
              testId="issuance-project" />
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Purpose *">
            <Input value={form.purpose} data-testid="issuance-purpose"
              onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              placeholder="e.g. PPE issue for July, Spare parts for Truck ZM-3400 …" />
          </Field>
        </div>

        <div className="mt-4">
          <div className="phizo-label mb-1.5">Items *</div>
          <div className="space-y-2">
            {form.items.map((it, i) => {
              const spare = isSpareCategory(it.sku);
              return (
                <div key={it._lineId || i} className="phizo-card p-2.5 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                    <div className="sm:col-span-6">
                      <div className="phizo-label mb-1">Item *</div>
                      <SearchableSelect
                        value={it.sku}
                        onChange={(v) => pickSku(i, v)}
                        options={inventoryOpts}
                        placeholder="Search by SKU or name…"
                        testId={`issuance-sku-${i}`}
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <div className="phizo-label mb-1">Qty *</div>
                      <Input type="number" step="0.01" value={it.quantity}
                        onChange={(e) => setItem(i, { quantity: e.target.value })}
                        data-testid={`issuance-qty-${i}`} />
                    </div>
                    <div className="sm:col-span-2">
                      <div className="phizo-label mb-1">Unit</div>
                      <Input value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })} />
                    </div>
                    <div className="sm:col-span-1 flex justify-end">
                      <BtnGhost onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })}
                        disabled={form.items.length === 1}
                        className="text-[#FF2A2A]">
                        <Trash size={12} weight="bold" />
                      </BtnGhost>
                    </div>
                  </div>
                  {spare && (
                    <div>
                      <div className="phizo-label mb-1">Equipment · required for Spares / Parts</div>
                      <SearchableSelect
                        value={it.equipment_ref ? `${it.equipment_type}:${it.equipment_ref}` : ""}
                        onChange={(_v, opt) => setItem(i, {
                          equipment_type: opt?.refType || "",
                          equipment_ref: opt?.refId || "",
                        })}
                        options={equipmentOpts}
                        placeholder="Pick the truck / machine this spare goes to…"
                        clearable
                        testId={`issuance-equipment-${i}`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <BtnGhost onClick={() => setForm({ ...form, items: [...form.items, { _lineId: crypto.randomUUID(), sku: "", name: "", quantity: 1, unit: "ea", equipment_type: "", equipment_ref: "" }] })}
            className="mt-2"
            data-testid="issuance-add-item">
            <Plus size={12} weight="bold" /> Add another line
          </BtnGhost>
        </div>

        <div className="mt-4">
          <Field label="Notes">
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
      </Modal>

      {/* View modal */}
      <Modal open={!!viewOpen} onClose={() => setViewOpen(null)}
        title={viewOpen ? `Issuance ${viewOpen.number}` : ""}
        actions={<BtnSecondary onClick={() => setViewOpen(null)}>Close</BtnSecondary>}
      >
        {viewOpen && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><div className="phizo-label">Date</div>{fmtDate(viewOpen.issue_date)}</div>
              <div><div className="phizo-label">Issued To</div>{viewOpen.issued_to_name}</div>
              <div><div className="phizo-label">Employee Code</div>{viewOpen.employee_code || "—"}</div>
              <div><div className="phizo-label">Department</div>{viewOpen.department || "—"}</div>
              <div><div className="phizo-label">Cost Centre</div>{viewOpen.cost_center || "—"}</div>
              <div><div className="phizo-label">Project</div>{viewOpen.project_code || "—"}</div>
              <div><div className="phizo-label">Issued By</div>{viewOpen.issued_by_name}</div>
              <div className="col-span-2"><div className="phizo-label">Purpose</div>{viewOpen.purpose}</div>
              {viewOpen.notes && (
                <div className="col-span-2"><div className="phizo-label">Notes</div>{viewOpen.notes}</div>
              )}
            </div>
            <div className="phizo-card mt-3">
              <div className="p-3 border-b border-[#e4e4e7]"><div className="phizo-label">Items</div></div>
              <table className="phizo-table">
                <thead>
                  <tr><th>SKU</th><th>Name</th><th className="text-right">Qty</th><th>Unit</th><th>Equipment</th></tr>
                </thead>
                <tbody>
                  {(viewOpen.items || []).map((it, i) => (
                    <tr key={i}>
                      <td className="mono">{it.sku}</td>
                      <td>{it.name}</td>
                      <td className="text-right mono font-semibold">{it.quantity}</td>
                      <td className="text-xs uppercase tracking-wider">{it.unit || "ea"}</td>
                      <td className="text-xs">
                        {it.equipment_ref ? `${(it.equipment_type || "").toUpperCase()} · ${it.equipment_ref.slice(0, 8)}…` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
